import React from "react";
import { useLocation } from "react-router";
import { NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Drawer,
  useMediaQuery,
  List,
  Typography,
  ListItem,
  Collapse,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { SidebarWidth } from "../../../assets/global/Theme-variable";
import LogoIcon from "../logo/LogoIcon";
import sidebarDiya from "../../../assets/images/sidebar-diya.png";
import sidebarTopDiwali from "../../../assets/images/sidebar-top-diwali.png";
import Menuitems from "./Menuitems";
// import Buynow from './Buynow';
import { useAuth } from "../../../authentication/AuthProvider";
import { manageFavoriteMenu } from "../../../services/sidebar";
import AboutApplicationDialog from "./AboutApplicationDialog";

const getAllNavigableMenuItems = (items = []) => {
  const result = [];

  items.forEach((item) => {
    // Add only actual clickable menu pages.
    // Do not add section headers such as GENERAL, IT Panel, etc.
    if (
      item?.title &&
      item?.href &&
      item.href !== "#" &&
      !item.navlabel &&
      !item.subheader
    ) {
      result.push(item);
    }

    if (Array.isArray(item?.children)) {
      result.push(...getAllNavigableMenuItems(item.children));
    }
  });

  return result;
};

const mapFavoritePathsToMenuItems = (favoriteData = []) => {
  if (!Array.isArray(favoriteData)) {
    return [];
  }

  const favoritePaths = favoriteData
    .map((favorite) => {
      if (typeof favorite === "string") {
        return favorite;
      }

      return favorite?.menuPath || favorite?.MenuPath;
    })
    .filter(Boolean);

  return favoritePaths
    .map((path) => resolveFavoriteMenuItem(path))
    .filter(Boolean);
};

const resolveFavoriteMenuItem = (favorite) => {
  const menuPath =
    typeof favorite === "string"
      ? favorite
      : favorite?.href || favorite?.menuPath || favorite?.MenuPath;

  if (!menuPath) {
    return null;
  }

  const menuItem = getAllNavigableMenuItems(Menuitems()).find(
    (item) => item.href === menuPath,
  );

  if (menuItem) {
    return menuItem;
  }

  // Fallback, so the row never appears blank.
  const fallbackTitle = menuPath
    .split("/")
    .filter(Boolean)
    .pop()
    ?.replace(/-/g, " ");

  return {
    href: menuPath,
    title: fallbackTitle || "Menu",
    icon: "circle",
  };
};

const Sidebar = ({ isMobileSidebarOpen, onSidebarClose, isSidebarOpen }) => {
  const [open, setOpen] = React.useState(true);
  const { pathname } = useLocation();
  const pathDirect = pathname;
  const pathWithoutLastPart = pathname.slice(0, pathname.lastIndexOf("/"));
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const [isHover, setIsHover] = React.useState(false);
  const auth = useAuth();
  const menuItems = Menuitems();
  const scrollRef = React.useRef(null);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [openAbout, setOpenAbout] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);
  const [subOpen, setSubOpen] = React.useState("");

  const [favorites, setFavorites] = React.useState([]);
  const [favoriteUpdatingPath, setFavoriteUpdatingPath] = React.useState("");

  const favoriteMenuItems = React.useMemo(
    () => favorites.map(resolveFavoriteMenuItem).filter(Boolean),
    [favorites],
  );

  const [recents, setRecents] = React.useState(() => {
    try {
      const saved = localStorage.getItem("sidebarRecents");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  React.useEffect(() => {
    localStorage.setItem("sidebarRecents", JSON.stringify(recents));
  }, [recents]);

  React.useEffect(() => {
    let isMounted = true;

    const loadFavorites = async () => {
      try {
        const response = await manageFavoriteMenu();

        if (isMounted) {
          setFavorites(mapFavoritePathsToMenuItems(response));
        }
      } catch (error) {
        console.error("Failed to load favorite menus:", error);

        if (isMounted) {
          setFavorites([]);
        }
      }
    };

    loadFavorites();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleFavorite = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();

    if (!item?.href || item.href === "#") {
      return;
    }

    if (favoriteUpdatingPath === item.href) {
      return;
    }

    const alreadyFavorite = favorites.some(
      (favorite) => favorite.href === item.href,
    );

    const previousFavorites = [...favorites];

    setFavorites((currentFavorites) => {
      if (alreadyFavorite) {
        return currentFavorites.filter(
          (favorite) => favorite.href !== item.href,
        );
      }

      return [item, ...currentFavorites];
    });

    setFavoriteUpdatingPath(item.href);

    try {
      const response = await manageFavoriteMenu({
        menuPath: item.href,
        isFavorite: !alreadyFavorite,
      });

      setFavorites(mapFavoritePathsToMenuItems(response));
    } catch (error) {
      console.error("Failed to update favorite menu:", error);

      setFavorites(previousFavorites);
    } finally {
      setFavoriteUpdatingPath("");
    }
  };

  const addRecent = (item) => {
    if (!item.href || item.href === "#") return;
    setRecents((prev) => {
      const exists = prev.find((rec) => rec.href === item.href);
      let newRecents = prev;
      if (exists) {
        newRecents = prev.filter((rec) => rec.href !== item.href);
      }
      newRecents = [item, ...newRecents].slice(0, 10);
      return newRecents;
    });
  };

  const handleClick = (index) => {
    const currentScroll = scrollRef.current?.scrollTop || 0;

    setOpen((previousOpen) => (previousOpen === index ? -1 : index));

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = currentScroll;
      }
    }, 0);
  };

  const handleSubClick = (title) => {
    setSubOpen((previousOpen) => (previousOpen === title ? "" : title));
  };

  const getSidebarCount = (child) => {
    if (child?.isEpoApprovalTotal) {
      return ` (${auth?.epoApprovalSidebarCount || 0})`;
    }

    if (child?.isCountMyApproval) {
      return ` (${auth?.myApprovalCount || 0})`;
    }

    if (child?.isCountMpcVote) {
      return ` (${auth?.MPCVoteCount || 0})`;
    }

    if (child?.isCountMpcDirector) {
      return ` (${auth?.mpcDirectorPendingCount || 0})`;
    }

    if (child?.isCountDirector) {
      return ` (${auth?.directorPendingCount || 0})`;
    }

    if (child?.isCountPresident) {
      return ` (${auth?.presidentPendingCount || 0})`;
    }

    if (child?.isTotal) {
      return ` (${auth?.documentAwaitingCount || 0})`;
    }

    return "";
  };

  const getFilteredMenuItems = () => {
    const query = searchQuery.trim().toLowerCase();

    if (query) {
      const getMatchingPages = (items) =>
        items.flatMap((item) => {
          if (!item.display) return [];

          if (item.children?.length) {
            return getMatchingPages(item.children);
          }

          if (
            !item.subheader &&
            item.title &&
            item.href &&
            item.href !== "#" &&
            item.title.toLowerCase().includes(query)
          ) {
            return [item];
          }

          return [];
        });

      return getMatchingPages(menuItems);
    }

    const filteredItems = menuItems.filter((item) => item.display);
    const seenSectionHeaders = new Set();

    return filteredItems.filter((item) => {
      if (!item.subheader) return true;

      const heading = item.subheader.trim().replace(/\s+/g, " ").toLowerCase();
      if (seenSectionHeaders.has(heading)) return false;

      seenSectionHeaders.add(heading);
      return true;
    });
  };

  const SidebarContent = (
    <Box
      sx={{
        height: "100dvh",
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          ml: -2,
          px: 2,
          pb: 1,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            mt: 2,
            ml: 1,
            pb: 1,
            textAlign: "center",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: -16,
              left: -16,
              right: -16,
              height: 90,
              backgroundImage: `url(${sidebarTopDiwali})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center top",
              backgroundSize: "100% 100%",
              pointerEvents: "none",
              zIndex: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
              marginTop: 2,
            }}
          >
            <Box sx={{ transform: "scale(1.5)" }}>
              <LogoIcon />
            </Box>
          </Box>
          <Box sx={{ mt: 2, px: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search all menu items "
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FeatherIcon icon="search" size="18" />
                  </InputAdornment>
                ),
              }}
            />
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              sx={{ mt: 1, minHeight: "40px" }}
            >
              <Tab
                label="Menu"
                sx={{
                  minWidth: 0,
                  px: 1,
                  minHeight: "40px",
                  py: 0,
                  fontSize: "0.75rem",
                }}
              />
              <Tab
                label="Favorites"
                sx={{
                  minWidth: 0,
                  px: 1,
                  minHeight: "40px",
                  py: 0,
                  fontSize: "0.75rem",
                }}
              />
              <Tab
                label="Recent"
                sx={{
                  minWidth: 0,
                  px: 1,
                  minHeight: "40px",
                  py: 0,
                  fontSize: "0.75rem",
                }}
              />
            </Tabs>
          </Box>
        </Box>
        <Box
          ref={scrollRef}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          sx={{
            flex: 1,
            height: "calc(100vh - 120px)",
            overflowY: "auto",
            overflowX: "hidden",
            pr: 2,
            mr: "-12px",

            "&::-webkit-scrollbar": {
              width: "6px",
            },

            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isHover ? "#bdbdbd" : "transparent",
              borderRadius: "10px",
              transition: "background-color 0.3s ease",
            },

            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          <Box>
            <List>
              {/* {Menuitems.map((item, index) => { */}
              {activeTab === 0
                ? getFilteredMenuItems().map((item, index) => {
                    const isParentActive = item.children?.some(
                      (child) =>
                        pathDirect === child.href ||
                        child?.subChild?.href?.some(
                          (x) => x === pathDirect || x === pathWithoutLastPart,
                        ),
                    );

                    // {/********SubHeader**********/}
                    if (item.subheader) {
                      return (
                        <li key={`section-${item.subheader}`}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="600"
                            sx={{
                              my: 2,
                              mt: 4,
                              ml: 2,
                              color: (theme) => theme.palette.primary.main,
                            }}
                          >
                            {item.subheader}
                          </Typography>
                        </li>
                      );
                      // {/********If Sub Menu**********/}
                      /* eslint no-else-return: "off" */
                    } else if (item.children) {
                      return (
                        <React.Fragment
                          key={`group-${item.title}-${item.href || index}`}
                        >
                          <ListItem
                            button
                            component="li"
                            onClick={() => handleClick(index)}
                            selected={isParentActive}
                            sx={{
                              mb: 1,
                              ml: 1,
                              ...(isParentActive && {
                                color: "white",
                                backgroundColor: (theme) =>
                                  `${theme.palette.primary.main}!important`,
                              }),
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                ...(isParentActive && {
                                  color: "white",
                                }),
                              }}
                            >
                              <FeatherIcon
                                icon={item.icon}
                                width="20"
                                height="20"
                              />
                            </ListItemIcon>
                            <ListItemText>{item.title}</ListItemText>
                            <Box component="span" sx={{ minWidth: 16 }}>
                              {index === open ? (
                                <FeatherIcon icon="chevron-down" size="16" />
                              ) : (
                                <FeatherIcon icon="chevron-right" size="16" />
                              )}
                            </Box>
                          </ListItem>
                          <Collapse in={index === open} timeout="auto">
                            <List component="li" disablePadding>
                              {item.children
                                .filter((x) => x.display)
                                .map((child) => {
                                  // --- CHANGED: Use <a> for external links, <NavLink> for internal ---
                                  if (
                                    child.href &&
                                    child.href.startsWith("http")
                                  ) {
                                    return (
                                      <ListItem
                                        key={child.title}
                                        button
                                        component="a"
                                        href={child.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => {
                                          addRecent(child);
                                          if (onSidebarClose) onSidebarClose(e);
                                        }}
                                        sx={{
                                          mb: 1,
                                          ml: 1,
                                        }}
                                      >
                                        <ListItemIcon
                                          sx={{
                                            svg: {
                                              width: "14px",
                                              marginLeft: "3px",
                                            },
                                          }}
                                        >
                                          <FeatherIcon
                                            icon={child.icon}
                                            width="20"
                                            height="20"
                                          />
                                        </ListItemIcon>
                                        <ListItemText>
                                          {child.title}
                                          {getSidebarCount(child)}
                                        </ListItemText>
                                        <IconButton
                                          onClick={(e) =>
                                            toggleFavorite(e, child)
                                          }
                                          size="small"
                                        >
                                          <FeatherIcon
                                            icon="star"
                                            size="16"
                                            fill={
                                              favorites.find(
                                                (f) => f.href === child.href,
                                              )
                                                ? "#ffc107"
                                                : "none"
                                            }
                                            color={
                                              favorites.find(
                                                (f) => f.href === child.href,
                                              )
                                                ? "#ffc107"
                                                : "inherit"
                                            }
                                          />
                                        </IconButton>
                                      </ListItem>
                                    );
                                  } else if (child.children) {
                                    // --- NEW: Handle 3rd layer sub-menus ---
                                    const isSubParentActive =
                                      child.children.some(
                                        (subChild) =>
                                          pathDirect === subChild.href ||
                                          subChild?.subChild?.href?.some(
                                            (x) =>
                                              x === pathDirect ||
                                              x === pathWithoutLastPart,
                                          ),
                                      );

                                    return (
                                      <React.Fragment key={child.title}>
                                        <ListItem
                                          button
                                          onClick={() =>
                                            handleSubClick(child.title)
                                          }
                                          selected={isSubParentActive}
                                          sx={{
                                            mb: 1,
                                            ml: 1,
                                            ...(isSubParentActive && {
                                              color: "white",
                                              backgroundColor: (theme) =>
                                                `${theme.palette.primary.main}!important`,
                                            }),
                                          }}
                                        >
                                          <ListItemIcon
                                            sx={{
                                              svg: {
                                                width: "14px",
                                                marginLeft: "3px",
                                              },
                                              ...(isSubParentActive && {
                                                color: "white",
                                              }),
                                            }}
                                          >
                                            <FeatherIcon
                                              icon={child.icon}
                                              width="20"
                                              height="20"
                                            />
                                          </ListItemIcon>
                                          <ListItemText>
                                            {child.title}
                                          </ListItemText>
                                          <Box
                                            component="span"
                                            sx={{ minWidth: 16 }}
                                          >
                                            {subOpen === child.title ? (
                                              <FeatherIcon
                                                icon="chevron-down"
                                                size="16"
                                              />
                                            ) : (
                                              <FeatherIcon
                                                icon="chevron-right"
                                                size="16"
                                              />
                                            )}
                                          </Box>
                                        </ListItem>
                                        <Collapse
                                          in={subOpen === child.title}
                                          timeout="auto"
                                        >
                                          <List component="li" disablePadding>
                                            {child.children
                                              .filter((x) => x.display)
                                              .map((subChild) => {
                                                const isSubChildActive =
                                                  pathDirect ===
                                                    subChild.href ||
                                                  subChild?.subChild?.href?.some(
                                                    (x) =>
                                                      x === pathDirect ||
                                                      x === pathWithoutLastPart,
                                                  );

                                                return (
                                                  <ListItem
                                                    key={subChild.title}
                                                    button
                                                    component={NavLink}
                                                    to={subChild.href}
                                                    onClick={(e) => {
                                                      addRecent(subChild);
                                                      if (onSidebarClose)
                                                        onSidebarClose(e);
                                                    }}
                                                    selected={isSubChildActive}
                                                    sx={{
                                                      mb: 1,
                                                      ml: 2,
                                                      ...(isSubChildActive && {
                                                        color: "white",
                                                        backgroundColor: (
                                                          theme,
                                                        ) =>
                                                          `${theme.palette.primary.main}!important`,
                                                      }),
                                                    }}
                                                  >
                                                    <ListItemIcon
                                                      sx={{
                                                        svg: {
                                                          width: "12px",
                                                          marginLeft: "6px",
                                                        },
                                                        ...(isSubChildActive && {
                                                          color: "white",
                                                        }),
                                                      }}
                                                    >
                                                      <FeatherIcon
                                                        icon={subChild.icon}
                                                        width="20"
                                                        height="20"
                                                      />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                      {subChild.title}
                                                    </ListItemText>
                                                    <IconButton
                                                      onClick={(e) =>
                                                        toggleFavorite(
                                                          e,
                                                          subChild,
                                                        )
                                                      }
                                                      size="small"
                                                    >
                                                      <FeatherIcon
                                                        icon="star"
                                                        size="16"
                                                        fill={
                                                          favorites.find(
                                                            (f) =>
                                                              f.href ===
                                                              subChild.href,
                                                          )
                                                            ? "#ffc107"
                                                            : "none"
                                                        }
                                                        color={
                                                          favorites.find(
                                                            (f) =>
                                                              f.href ===
                                                              subChild.href,
                                                          )
                                                            ? "#ffc107"
                                                            : "inherit"
                                                        }
                                                      />
                                                    </IconButton>
                                                  </ListItem>
                                                );
                                              })}
                                          </List>
                                        </Collapse>
                                      </React.Fragment>
                                    );
                                  } else {
                                    return (
                                      <ListItem
                                        key={child.title}
                                        button
                                        component={NavLink}
                                        to={child.href}
                                        onClick={(e) => {
                                          addRecent(child);
                                          if (onSidebarClose) onSidebarClose(e);
                                        }}
                                        selected={
                                          pathDirect === child.href ||
                                          child?.subChild?.href?.some(
                                            (x) =>
                                              x === pathDirect ||
                                              x === pathWithoutLastPart,
                                          )
                                        }
                                        sx={{
                                          mb: 1,
                                          ml: 1,
                                          ...((pathDirect === child.href ||
                                            child?.subChild?.href?.some(
                                              (x) =>
                                                x === pathDirect ||
                                                x === pathWithoutLastPart,
                                            )) && {
                                            color: "white",
                                            backgroundColor: (theme) =>
                                              `${theme.palette.primary.main}!important`,
                                          }),
                                        }}
                                      >
                                        <ListItemIcon
                                          sx={{
                                            svg: {
                                              width: "14px",
                                              marginLeft: "3px",
                                            },
                                            ...((pathDirect === child.href ||
                                              child?.subChild?.href?.some(
                                                (x) =>
                                                  x === pathDirect ||
                                                  x === pathWithoutLastPart,
                                              )) && {
                                              color: "white",
                                            }),
                                          }}
                                        >
                                          <FeatherIcon
                                            icon={child.icon}
                                            width="20"
                                            height="20"
                                          />
                                        </ListItemIcon>
                                        <ListItemText>
                                          {child.title}
                                          {getSidebarCount(child)}
                                        </ListItemText>
                                        <IconButton
                                          onClick={(e) =>
                                            toggleFavorite(e, child)
                                          }
                                          size="small"
                                        >
                                          <FeatherIcon
                                            icon="star"
                                            size="16"
                                            fill={
                                              favorites.find(
                                                (f) => f.href === child.href,
                                              )
                                                ? "#ffc107"
                                                : "none"
                                            }
                                            color={
                                              favorites.find(
                                                (f) => f.href === child.href,
                                              )
                                                ? "#ffc107"
                                                : "inherit"
                                            }
                                          />
                                        </IconButton>
                                      </ListItem>
                                    );
                                  }
                                })}
                            </List>
                          </Collapse>
                        </React.Fragment>
                      );
                      // {/********If Sub No Menu**********/}
                    } else {
                      return (
                        <List
                          component="li"
                          disablePadding
                          key={`item-${item.title}-${item.href || index}`}
                        >
                          {item.href && item.href.startsWith("http") ? (
                            <ListItem
                              button
                              component="a"
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                mb: 1,
                                ml: 1,
                              }}
                            >
                              <ListItemIcon>
                                <FeatherIcon
                                  icon={item.icon}
                                  width="20"
                                  height="20"
                                />
                              </ListItemIcon>
                              <ListItemText
                                onClick={(e) => {
                                  addRecent(item);
                                  if (onSidebarClose) onSidebarClose(e);
                                }}
                              >
                                {item.title}
                              </ListItemText>
                              <IconButton
                                onClick={(e) => toggleFavorite(e, item)}
                                size="small"
                              >
                                <FeatherIcon
                                  icon="star"
                                  size="16"
                                  fill={
                                    favorites.find((f) => f.href === item.href)
                                      ? "#ffc107"
                                      : "none"
                                  }
                                  color={
                                    favorites.find((f) => f.href === item.href)
                                      ? "#ffc107"
                                      : "inherit"
                                  }
                                />
                              </IconButton>
                            </ListItem>
                          ) : (
                            <ListItem
                              button
                              component={NavLink}
                              to={item.href}
                              onClick={(e) => {
                                addRecent(item);
                                if (onSidebarClose) onSidebarClose(e);
                              }}
                              selected={pathDirect === item.href}
                              sx={{
                                mb: 1,
                                ml: 1,
                                ...(pathDirect === item.href && {
                                  color: "white",
                                  backgroundColor: (theme) =>
                                    `${theme.palette.primary.main}!important`,
                                }),
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  ...(pathDirect === item.href && {
                                    color: "white",
                                  }),
                                }}
                              >
                                <FeatherIcon
                                  icon={item.icon}
                                  width="20"
                                  height="20"
                                />
                              </ListItemIcon>
                              <ListItemText onClick={onSidebarClose}>
                                {item.title}
                              </ListItemText>
                              <IconButton
                                onClick={(e) => toggleFavorite(e, item)}
                                size="small"
                              >
                                <FeatherIcon
                                  icon="star"
                                  size="16"
                                  fill={
                                    favorites.find((f) => f.href === item.href)
                                      ? "#ffc107"
                                      : "none"
                                  }
                                  color={
                                    favorites.find((f) => f.href === item.href)
                                      ? "#ffc107"
                                      : "inherit"
                                  }
                                />
                              </IconButton>
                            </ListItem>
                          )}
                        </List>
                      );
                    }
                  })
                : (activeTab === 1 ? favoriteMenuItems : recents).map(
                    (child, index) => {
                      return (
                        <ListItem
                          key={child.title + index}
                          button
                          component={NavLink}
                          to={child.href}
                          onClick={(e) => {
                            addRecent(child);
                            if (onSidebarClose) onSidebarClose(e);
                          }}
                          selected={pathDirect === child.href}
                          sx={{
                            mb: 1,
                            ml: 1,
                            ...(pathDirect === child.href && {
                              color: "white",
                              backgroundColor: (theme) =>
                                `${theme.palette.primary.main}!important`,
                            }),
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              ...(pathDirect === child.href && {
                                color: "white",
                              }),
                            }}
                          >
                            <FeatherIcon
                              icon={child.icon}
                              width="20"
                              height="20"
                            />
                          </ListItemIcon>
                          <ListItemText>{child.title}</ListItemText>
                          <IconButton
                            onClick={(e) => toggleFavorite(e, child)}
                            size="small"
                          >
                            <FeatherIcon
                              icon="star"
                              size="16"
                              fill={
                                favorites.find((f) => f.href === child.href)
                                  ? "#ffc107"
                                  : "none"
                              }
                              color={
                                favorites.find((f) => f.href === child.href)
                                  ? "#ffc107"
                                  : "inherit"
                              }
                            />
                          </IconButton>
                        </ListItem>
                      );
                    },
                  )}
            </List>
          </Box>
        </Box>
        {/* <Buynow /> */}
      </Box>

      {/* Footer: About Application stays above the full-width Diwali artwork. */}
      <Box
        sx={{
          width: "100%",
          flexShrink: 0,
          m: 0,
          marginTop: "auto",
          p: 0,
          overflow: "hidden",
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 0.5,
            textAlign: "center",
          }}
        >
          <Button
            fullWidth
            variant="text"
            color="inherit"
            onClick={() => setOpenAbout(true)}
            startIcon={<FeatherIcon icon="info" size="18" />}
            sx={{
              minHeight: 42,
              color: "text.secondary",
              justifyContent: "flex-start",
              px: 2,
              textTransform: "none",
              fontWeight: 500,
              position: "relative",
              zIndex: 1,
            }}
          >
            About Application
          </Button>
        </Box>

        <Box
          component="img"
          src={sidebarDiya}
          alt=""
          aria-hidden="true"
          aria-label={undefined}
          draggable={false}
          sx={{
            display: "block",
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%",
            height: "auto",
            maxHeight: "none",
            objectFit: "cover",
            objectPosition: "center bottom",
            m: 0,
            p: 0,
            verticalAlign: "bottom",
            lineHeight: 0,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      </Box>

      {/* 🔹 About Dialog Popup */}
      <AboutApplicationDialog
        open={openAbout}
        onClose={() => setOpenAbout(false)}
      />
    </Box>
  );
  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open={isSidebarOpen}
        variant="persistent"
        PaperProps={{
          sx: {
            width: SidebarWidth,
            border: "0 !important",
            boxShadow: "0px 7px 30px 0px rgb(113 122 131 / 11%)",
            overflow: "hidden",
          },
        }}
      >
        {SidebarContent}
      </Drawer>
    );
  }
  return (
    <Drawer
      anchor="left"
      open={isMobileSidebarOpen}
      onClose={onSidebarClose}
      PaperProps={{
        sx: {
          width: SidebarWidth,
          border: "0 !important",
        },
      }}
      variant="temporary"
    >
      {SidebarContent}
    </Drawer>
  );
};

Sidebar.propTypes = {
  isMobileSidebarOpen: PropTypes.bool,
  onSidebarClose: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
};

export default Sidebar;
